/**
 * Per-user display preferences — `/api/v1/me/settings`.
 *
 * Server-side rather than `localStorage`, so a preference follows the person
 * across machines instead of the browser. Neither endpoint takes a user id:
 * they always act on the caller, so reading someone else's preferences is not
 * a request that can be expressed.
 */

import type { components } from "./_generated/schema";
import { apiFetch } from "./apiFetch";

export type SettingsOut = components["schemas"]["SettingsOut"];
export type SettingsIn = components["schemas"]["SettingsIn"];

export const THEMES = ["light", "dark"] as const;
export const FONT_SIZES = ["sm", "md", "lg", "xl"] as const;
export const FONT_FAMILIES = ["system", "inter", "serif", "mono"] as const;

/** What a user who has never opened the settings page sees. Mirrors the
 *  backend's `DEFAULT_SETTINGS` so the first paint matches what is stored —
 *  and `dark` matches the MantineProvider's `defaultColorScheme`, so the
 *  signed-out and never-configured states look the same. */
export const DEFAULT_SETTINGS: SettingsOut = {
  theme: "dark",
  font_size: "md",
  font_family: "system",
};

export function getMySettings(token: string): Promise<SettingsOut> {
  return apiFetch<SettingsOut>("/api/v1/me/settings", { token });
}

export function putMySettings(token: string, input: SettingsIn): Promise<SettingsOut> {
  return apiFetch<SettingsOut>("/api/v1/me/settings", { method: "PUT", token, json: input });
}
